<template>
  <div>
    <v-card>
      <v-card-title>Leave Reports by Individual</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="leaveType"
                :loading="loading"
                label="Leave Type"
                :items="leaveTypes"
                :error-messages="errors.collect('leaveType')"
                data-vv-name="leaveType"  
                data-vv-as="leave type"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="name"
                item-value="id"
                small-chips
                clearable
              />
            </v-col>

            <v-col>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :disabled="years.length < 1"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDateDisplay"
                    v-validate="'required'"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    :error-messages="errors.collect('startDateDisplay')"
                    data-vv-name="startDateDisplay"  
                    data-vv-as="start date"    
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="minStartDate"
                  :max="maxStartDate"
                  @input="startDateChange"
                />
              </v-menu>
            </v-col>

            <v-col>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :disabled="years.length < 1"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDateDisplay"
                    v-validate="'required'"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    :error-messages="errors.collect('endDateDisplay')"
                    data-vv-name="endDateDisplay"  
                    data-vv-as="end date"  
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  :min="startDate"
                  :max="maxEndDate"
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="state"
                :loading="loading"
                label="States"
                :items="states"
                :error-messages="errors.collect('state')"
                data-vv-name="state"  
                data-vv-as="states"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="name"
                item-value="id"
                small-chips
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="department"
                :loading="loading"
                label="Departments"
                :items="departments"
                :error-messages="errors.collect('department')"
                data-vv-name="department"  
                data-vv-as="departments"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="name"
                item-value="id"
                small-chips
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="employee"
                :loading="loading"
                label="Employees"
                :items="filteredEmployees"
                :error-messages="errors.collect('employee')"
                data-vv-name="employee"  
                data-vv-as="employee"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="person.name"
                item-value="employee_uid"
                small-chips
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                :loading="progressing"
                :disabled="years.length < 1"
                color="primary"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row> 
        </v-col>
      </v-row>
    </v-card> 
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        loading: false,
        progressing: false,
        state: null,
        states: [],
        department: null,
        departments: [],
        years: [], 
        minYear: null,
        maxYear: null,
        startDateMenu: false,
        startDate: this.$moment().startOf('month').format('YYYY-MM-DD'),
        endDateMenu: false,
        endDate: this.$moment().format('YYYY-MM-DD'),              
        leaveTypes: [],
        leaveType: null,
        employee: null,
        employees: [],
        progressbar: 0,
      }
    },

    computed: {
      filteredEmployees () {
        if (_.size(this.department) > 0) {
          return this.employees.filter(employee => {
            if (this.department) { return this.department.includes(employee.department_id) }
          })
        }
        return this.employees
      },
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      maxEndDate () {
        return this.startDate ? this.$moment(this.startDate).endOf('year').format('YYYY-MM-DD') : this.$moment().endOf('year').format('YYYY-MM-DD')
      },   
      minStartDate () {
        return this.$moment([this.minYear]).format('YYYY-MM-DD')
      },
      maxStartDate () {
        return this.$moment([this.maxYear]).endOf('year').format('YYYY-MM-DD')
      },            
    },

    created () {
      this.loading = true
        
      Promise.all([
        this.$api.hrisDepartments().list(['name'], false),
        this.$api.hrisLeaveTypes().list(),
        this.$api.states().list(),
        this.$api.leaveEntitlements().listEntitlementYear(),
        this.$api.employees().list(),
      ])
        .then(([departments, leaveTypes, states, leaveEntitlements, employees]) => {
          this.departments = departments.data        
          this.leaveTypes = leaveTypes.data        
          this.states = states.data
          this.years = leaveEntitlements.data
          this.minYear = Math.min(...this.years)  
          this.maxYear = Math.max(...this.years)  
          this.employees = _.orderBy(employees.data, ['person.name'], ['asc'])

          this.loading = false
        })
    },

    methods: {
      startDateChange () {
        this.startDateMenu = false
        this.endDate = this.$moment(this.startDate).endOf('month').format('YYYY-MM-DD')
      },          
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.leaveReports().generateRemainingLeaveBalance(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let year = this.$moment(this.startDate).format('YYYY')

                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Leave Reports by Individual-' + year + '.xlsx')
                link.click()

                this.progressing = false
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              }).catch(() => {
                this.progressing = false

                this.$snack({ color: 'error', message: 'Error! Please contact support' })
              })
          })
      },

      prepareFormData () {
        return {
          states: this.state,
          departments: this.department,
          leaveTypes: this.leaveType,
          start_date: this.startDate,
          end_date: this.endDate,          
          employees: this.employee,
          isSubordinatesReport: false,
        }
      },
    },
  }
</script>
